import React, { FC, useRef, useState, useCallback, useEffect, SyntheticEvent } from 'react';
import { getSide, showFloaty, getNubPos, getBubPos } from './utils';
import { useOnClickOutside } from '../util';
import { TOOLTIP_SIDES } from '../constants';
import { sanitizeDOM } from '@trip-ux/common';

export type TooltipProps = {
  id?: string;
  side?: TOOLTIP_SIDES;
  ariaLabel?: string;
  disableAriaForPopup?: boolean;
  title?: string;
  bubbleWidth: number;
  hover?: boolean;
  hasCloseButton?: boolean;
  closeButtonAria?: string;
  children?: React.ReactNode;
};

export const Tooltip: FC<TooltipProps> = ({
  id,
  side = null,
  ariaLabel,
  disableAriaForPopup,
  title,
  bubbleWidth,
  hover = true,
  hasCloseButton,
  closeButtonAria,
  children
}) => {
  const toggleRef = useRef(null);
  const [isShowTooltip, setIsShowTooltip] = useState(false);
  const [calculatedSide, setCalculatedSide] = useState(side);

  const memPopTip = useCallback(() => {
    setCalculatedSide(getSide(toggleRef, side, bubbleWidth));
    if (hover) {
      setIsShowTooltip(true);
    }
  }, [toggleRef, side, bubbleWidth, hover]);

  const wrapperRef = useRef(null);
  useOnClickOutside(wrapperRef, () => setIsShowTooltip(false));

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isShowTooltip) {
        setIsShowTooltip(false);
        toggleRef.current?.focus();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isShowTooltip]);

  const onClickOrTouchButton = useCallback((e: SyntheticEvent) => {
    e.preventDefault();
    toggleRef.current.focus();
    setCalculatedSide(getSide(toggleRef, side, bubbleWidth));
    setIsShowTooltip(!isShowTooltip);
  }, []);

  const closeTooltip = useCallback((e: SyntheticEvent) => {
    e.preventDefault();
    setIsShowTooltip(false);
    toggleRef.current.focus();
  }, []);

  return (
    <div
      onMouseOut={() => hover && setIsShowTooltip(false)}
      onBlur={() => !hasCloseButton && setIsShowTooltip(false)}
      ref={wrapperRef}
      className="tooltip-wrapper">
      <button
        id={`${id}-button`}
        data-testid={`${id}-button`}
        className="tooltip-button"
        ref={toggleRef}
        onFocus={memPopTip}
        onMouseOver={memPopTip}
        onClick={onClickOrTouchButton}
        onTouchEnd={onClickOrTouchButton}
        aria-label={ariaLabel}
        aria-labelledby={isShowTooltip ? `${id}-content` : undefined}
        aria-expanded={disableAriaForPopup ? null : isShowTooltip}
        type="button">
        {children || <span aria-hidden="true" className="tooltip-icon" />}
      </button>
      {isShowTooltip && (
        <>
          {showFloaty(calculatedSide) && (
            <span data-testid="arrow" className="tooltip-arrow" style={getNubPos(calculatedSide)} />
          )}
          <span
            data-testid="side-arrow"
            className={`
              tooltip-side-arrow
              ${calculatedSide === TOOLTIP_SIDES.RIGHT && 'right-side'}
              ${calculatedSide === TOOLTIP_SIDES.LEFT && 'left-side'}
            `}
            style={getNubPos(calculatedSide)}
          />
          <span
            id={`${id}-content-wrapper`}
            data-testid={`${id}-content-wrapper`}
            className="tooltip-content-wrapper"
            onClick={hasCloseButton ? null : () => setIsShowTooltip(false)}>
            <span className="tooltip-content" style={getBubPos(calculatedSide, bubbleWidth)}>
              <p id={`${id}-content`} dangerouslySetInnerHTML={{ __html: sanitizeDOM(title) }} />
              {hasCloseButton && (
                <button
                  id={`${id}-close`}
                  data-testid={`${id}-close`}
                  className="tooltip-close-button"
                  aria-label={closeButtonAria}
                  onClick={closeTooltip}>
                  <span aria-hidden="true" className="tooltip-close-icon" />
                </button>
              )}
            </span>
          </span>
        </>
      )}
    </div>
  );
};
